import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { peekClips, takeClips, normalizeStashedClips, hasFullLocalSet } from '../clipStore';
import { PROCESS_MIN } from '../captureFlowGuide';
import { reelsPath } from '../paths';

const RENDER_LABELS = {
  composing: 'Composing your Instagram reel…',
  rendering: 'Rendering with motion graphics…',
  uploading: 'Saving your reel…',
  completed: 'Ready!',
};

/**
 * Background-safe loader: resume from server if local blobs are gone.
 * Safe to leave / refresh — reopen from Video home to check status.
 */
export default function CreatingReel() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const runRef = useRef(0);
  const pollRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Getting things ready…');
  const [error, setError] = useState('');
  const [failed, setFailed] = useState(false);
  const [canRetryUpload, setCanRetryUpload] = useState(false);

  useEffect(() => {
    const runId = ++runRef.current;
    setError('');
    setFailed(false);
    setCanRetryUpload(false);
    setProgress(0);
    setLabel('Getting things ready…');

    const clearPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const pollRender = () =>
      new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let networkMisses = 0;
        const tick = async () => {
          if (runId !== runRef.current) {
            clearPoll();
            resolve('cancelled');
            return;
          }
          try {
            const session = await api.getSession(sessionId);
            networkMisses = 0;
            if (runId !== runRef.current) return;

            if (session.status === 'completed' || session.status === 'approved') {
              setProgress(100);
              setLabel('Ready!');
              clearPoll();
              resolve('done');
              return;
            }
            if (session.status === 'failed' || session.status === 'expired') {
              clearPoll();
              reject(new Error(session.error_message || 'Could not create your reel.'));
              return;
            }

            const serverPct = Math.max(0, Math.min(100, session.progress ?? 0));
            setProgress(Math.round(50 + serverPct * 0.48));
            setLabel(RENDER_LABELS[session.stage] || 'Creating your reel…');

            if (serverPct <= 10 && Date.now() - startedAt > 120000) {
              clearPoll();
              reject(new Error('Still starting. Open Video home to check status, or try again.'));
            }
          } catch (e) {
            networkMisses += 1;
            if (networkMisses >= 8) {
              clearPoll();
              reject(
                new Error(
                  'Connection lost while creating your reel. It may still finish — open Video home or tap Try again.'
                )
              );
              return;
            }
            setLabel('Reconnecting…');
          }
        };
        tick();
        pollRef.current = setInterval(tick, 1500);
      });

    (async () => {
      try {
        const rawClips = peekClips(sessionId);
        const clips = normalizeStashedClips(rawClips);
        // Batch-upload on-device clips after capture — nothing is uploaded during recording.
        let needUpload = hasFullLocalSet(clips, PROCESS_MIN);

        if (needUpload) {
          const uploadJobs = [
            { label: 'Before', type: 'before', blob: clips.before, sequence: 0 },
            ...clips.process.map((item) => ({
              label: `Cut ${item.sequence + 1}`,
              type: 'process',
              blob: item.blob,
              sequence: item.sequence,
            })),
            { label: 'After', type: 'result', blob: clips.result, sequence: 0 },
          ];

          setLabel(`Uploading ${uploadJobs.length} clips…`);
          setProgress(5);
          // Upload in parallel — much faster than one-by-one on slow links.
          const results = await Promise.all(
            uploadJobs.map(async (job, i) => {
              const res = await api.uploadClip(sessionId, job.type, job.blob, job.sequence);
              if (runId !== runRef.current) return null;
              setProgress(Math.round(((i + 1) / uploadJobs.length) * 40));
              setLabel(`Uploaded ${job.label} (${i + 1}/${uploadJobs.length})`);
              return { job, res };
            })
          );
          if (runId !== runRef.current) return;
          for (const item of results) {
            if (!item) continue;
            const status = String(item.res.validation?.status || 'pass').toLowerCase();
            if (status === 'retake' || status === 'error') {
              setCanRetryUpload(true);
              throw new Error(pickValidationError(item.res.validation, item.job.label));
            }
          }
          setProgress(40);
          takeClips(sessionId);
        } else if (rawClips) {
          // Incomplete local stash — prefer server session clips
          takeClips(sessionId);
        }

        if (runId !== runRef.current) return;
        const session = await api.getSession(sessionId);
        if (runId !== runRef.current) return;

        if (session.status === 'completed' || session.status === 'approved') {
          navigate(reelsPath('preview', sessionId), { replace: true });
          return;
        }

        if (session.status === 'rendering' || session.status === 'processing' || session.status === 'queued') {
          setProgress(Math.round(50 + Math.max(0, session.progress ?? 0) * 0.48));
          setLabel(RENDER_LABELS[session.stage] || 'Creating your reel…');
        } else {
          setLabel('Preparing your reel…');
          setProgress(44);
          const ready = await api.validate(sessionId);
          if (runId !== runRef.current) return;
          if (!ready?.ready) {
            const missing = ready?.missing?.length
              ? `Still need: ${ready.missing.join(', ')}`
              : 'Footage is not ready yet. Continue capture from Video home.';
            const detail = [
              ready?.has_before === false ? 'before' : null,
              ready?.process_count != null ? `process ${ready.process_count}` : null,
              ready?.has_result === false ? 'result' : null,
            ]
              .filter(Boolean)
              .join(' · ');
            setCanRetryUpload(Boolean(peekClips(sessionId)));
            throw new Error(detail ? `${missing} (${detail})` : missing);
          }
          setLabel('Starting your reel…');
          setProgress(48);
          await api.render(sessionId);
          if (runId !== runRef.current) return;
          setProgress(50);
          setLabel('Creating your reel…');
        }

        const result = await pollRender();
        if (runId !== runRef.current || result === 'cancelled') return;
        await wait(400);
        if (runId === runRef.current) {
          navigate(reelsPath('preview', sessionId), { replace: true });
        }
      } catch (e) {
        if (runId !== runRef.current) return;
        clearPoll();
        setFailed(true);
        setError(e.message || 'Something went wrong');
        setLabel('Something went wrong');
        setCanRetryUpload((prev) => prev || Boolean(peekClips(sessionId)));
      }
    })();

    return () => {
      if (runRef.current === runId) runRef.current += 1;
      clearPoll();
    };
  }, [sessionId, navigate, attempt]);

  return (
    <main className="upload-page">
      <p className="eyebrow">Naturals</p>
      <h1 className="brand" style={{ fontSize: '2rem' }}>
        {failed ? 'Needs attention' : 'Creating your reel'}
      </h1>
      <p className="lede">
        {failed
          ? 'Retry here, go back to capture, or open Video home.'
          : 'Uploading clips, then creating your reel. Safe to leave — reopen from Video home anytime.'}
      </p>

      <div className="upload-hero panel">
        <div
          className={`upload-ring ${failed ? 'bad' : 'spin'} ${progress >= 100 && !failed ? 'ok' : ''}`}
          style={{ '--p': `${progress}%` }}
        >
          <span className="upload-pct">{failed ? '!' : `${progress}%`}</span>
        </div>
        <p className="upload-label">{label}</p>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="actions">
        {failed ? (
          <>
            <button className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>
              {canRetryUpload ? 'Retry' : 'Try again'}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => navigate(reelsPath('capture', sessionId), { replace: true })}
            >
              Back to capture
            </button>
          </>
        ) : null}
        <button className="btn btn-ghost" onClick={() => navigate(reelsPath())}>
          Video home
        </button>
      </div>
    </main>
  );
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function pickValidationError(validation, label) {
  const messages = validation?.messages || [];
  const actionable = messages.find((m) => m && !String(m).startsWith('✓'));
  if (actionable) return actionable;
  if (validation?.errors?.length) {
    return `${label}: ${validation.errors[0]}`;
  }
  return `Please retake the ${label} clip.`;
}
